/**
 * DIAS Master Checklist — auto-validating tick-box page that runs ~30 checks
 * across the 5 DIAS modules and surfaces every red/amber item with a fix
 * suggestion. Manual attestations (fire drill done, IPC audit done, etc.)
 * are tickable inline and persist in `db.dias_attestations`.
 *
 * Designed for the QIP-readiness sit-down: print-friendly, exportable to CSV,
 * and module-grouped so the assessor can walk Standard-by-Standard.
 */
import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Shield, CheckCircle2, AlertTriangle, XCircle, Circle, Loader2,
  Download, Printer, RefreshCw, Sparkles, ListChecks,
} from 'lucide-react';
import Header from '../components/Header';
import BulkActions from '../components/BulkActions';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const C = '#42CDD7';

const STATUS_META = {
  pass:        { color: '#16A34A', icon: CheckCircle2,  label: 'Pass' },
  fail:        { color: '#DC2626', icon: XCircle,        label: 'Fail' },
  warning:     { color: '#D97706', icon: AlertTriangle,  label: 'Review' },
  manual_only: { color: '#6B7280', icon: Circle,         label: 'Attest' },
};

export default function DIASChecklistPage() {
  const { user } = useAuth();
  const isPM = user?.role === 'admin' || user?.role === 'superuser';
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [filter, setFilter] = useState('all'); // all | open | manual
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true); setErr(null);
    try {
      const r = await axios.get(`${API}/dias-validator`, { withCredentials: true });
      setReport(r.data);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Failed to load validator.');
    }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const attest = async (check_id, ticked, note = '') => {
    setBusyId(check_id);
    try {
      await axios.post(`${API}/dias-validator/attest`, { check_id, ticked, note }, { withCredentials: true });
      await load();
    } catch (e) {
      setErr(e.response?.data?.detail || 'Attestation failed.');
    }
    setBusyId(null);
  };

  const filtered = useMemo(() => {
    if (!report) return [];
    return report.modules.map(m => ({
      ...m,
      checks: m.checks.filter(c => {
        if (filter === 'open') return c.status === 'fail' || c.status === 'warning' || c.status === 'manual_only';
        if (filter === 'manual') return c.manual;
        return true;
      }),
    })).filter(m => m.checks.length > 0);
  }, [report, filter]);

  const downloadCsv = () => {
    if (!report) return;
    const rows = [['Module', 'Check', 'Status', 'Detail', 'Suggestion']];
    report.modules.forEach(m => m.checks.forEach(c => {
      rows.push([m.label, c.label, c.status, (c.detail || '').replace(/\n/g, ' '), (c.suggestion || '').replace(/\n/g, ' ')]);
    }));
    const csv = rows.map(r => r.map(v => `"${(v ?? '').toString().replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `DIAS_master_checklist_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click();
    a.remove(); URL.revokeObjectURL(url);
  };

  if (!isPM) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="max-w-3xl mx-auto px-4 py-12 text-center">
          <Shield className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
          <p className="text-sm text-muted-foreground">DIAS Master Checklist is restricted to Practice Managers.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="dias-checklist-page">
      <Header />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full mb-3" style={{ backgroundColor: `${C}1A`, color: C }}>
              <ListChecks className="w-3.5 h-3.5" />
              <span className="text-[10px] font-bold uppercase tracking-wider">DIAS master checklist</span>
            </div>
            <h1 className="font-['Outfit'] text-4xl sm:text-5xl font-bold text-foreground">QIP pre-flight check</h1>
            <p className="mt-3 text-sm sm:text-base text-muted-foreground max-w-2xl">
              Auto-validating tick-box across the 5 DIAS modules. Every red/amber row has a one-line fix. Manual items (fire drill, IPC audit, ARPANSA submission) are ticked inline and persist for 12 months.
            </p>
          </div>

          <div className="flex items-center gap-2 print:hidden">
            <button onClick={load} disabled={loading} data-testid="dias-checklist-refresh"
              className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1.5 rounded-md border disabled:opacity-50"
              style={{ borderColor: `${C}40`, color: C }}>
              {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />} Refresh
            </button>
            <button onClick={downloadCsv} data-testid="dias-checklist-csv"
              className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1.5 rounded-md border"
              style={{ borderColor: `${C}40`, color: C }}>
              <Download className="w-3 h-3" /> CSV
            </button>
            <button onClick={() => window.print()} data-testid="dias-checklist-print"
              className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1.5 rounded-md border"
              style={{ borderColor: `${C}40`, color: C }}>
              <Printer className="w-3 h-3" /> Print
            </button>
            <BulkActions resource="dias_attestations" title="DIAS attestations" readOnly color={C} />
          </div>
        </div>

        {err && (
          <div className="mb-4 rounded-lg border border-red-500/30 bg-red-500/5 p-3 text-xs text-red-600" data-testid="dias-checklist-error">
            {err}
          </div>
        )}

        {report && <SummaryStrip report={report} />}

        {report && (
          <div className="flex items-center gap-2 my-4 print:hidden" data-testid="dias-checklist-filters">
            {[
              { id: 'all',    label: `All (${report.total_checks})` },
              { id: 'open',   label: `Needs action (${report.counts.fail + report.counts.warning + report.counts.manual_only})` },
              { id: 'manual', label: 'Manual attestations only' },
            ].map(f => (
              <button key={f.id} onClick={() => setFilter(f.id)}
                data-testid={`dias-filter-${f.id}`}
                className={`text-[11px] font-semibold px-3 py-1.5 rounded-full border transition ${
                  filter === f.id
                    ? 'text-white'
                    : 'text-muted-foreground hover:text-foreground border-border'
                }`}
                style={filter === f.id ? { backgroundColor: C, borderColor: C } : {}}>
                {f.label}
              </button>
            ))}
          </div>
        )}

        {loading && !report && (
          <div className="bg-card border border-border rounded-2xl p-6 animate-pulse space-y-3">
            <div className="h-5 w-1/3 bg-muted rounded" />
            <div className="h-3 w-2/3 bg-muted/60 rounded" />
            <div className="h-3 w-1/2 bg-muted/60 rounded" />
          </div>
        )}

        {filtered.map(m => (
          <ModuleBlock key={m.label} module={m} attest={attest} busyId={busyId} />
        ))}

        {report && filtered.length === 0 && (
          <div className="bg-card border border-border rounded-2xl p-8 text-center" data-testid="dias-checklist-empty">
            <CheckCircle2 className="w-8 h-8 mx-auto text-green-600 mb-2" />
            <p className="text-sm font-semibold text-foreground">Nothing matches the current filter.</p>
            <p className="text-xs text-muted-foreground">Switch to "All" to see every check.</p>
          </div>
        )}

        <p className="mt-6 text-[11px] text-muted-foreground inline-flex items-center gap-1.5">
          <Sparkles className="w-3 h-3" /> As of {report ? new Date(report.as_of).toLocaleString('en-AU') : '—'}. Manual attestations expire 12 months from tick-date.
        </p>
      </div>
    </div>
  );
}

function SummaryStrip({ report }) {
  const buckets = [
    { id: 'pass',        ...STATUS_META.pass },
    { id: 'fail',        ...STATUS_META.fail },
    { id: 'warning',     ...STATUS_META.warning },
    { id: 'manual_only', ...STATUS_META.manual_only },
  ];
  const overallColor = report.pass_pct >= 90 ? '#16A34A' : report.pass_pct >= 70 ? '#0EA5E9' : report.pass_pct >= 50 ? '#D97706' : '#DC2626';
  return (
    <div className="bg-card border border-border rounded-2xl p-5" data-testid="dias-checklist-summary">
      <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-center">
        <div className="text-center md:border-r md:border-border md:pr-3">
          <div className="text-4xl font-bold tabular-nums" style={{ color: overallColor }}>{report.pass_pct}%</div>
          <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Audit-ready</div>
          <div className="text-[10px] text-muted-foreground mt-0.5">{report.total_checks} total checks</div>
        </div>
        {buckets.map(b => {
          const Icon = b.icon;
          const n = report.counts[b.id] || 0;
          return (
            <div key={b.id} className="flex items-center gap-2 px-2 py-1.5 rounded-lg" data-testid={`dias-count-${b.id}`}
              style={{ backgroundColor: `${b.color}10`, border: `1px solid ${b.color}30` }}>
              <Icon className="w-4 h-4 shrink-0" style={{ color: b.color }} />
              <div className="min-w-0">
                <div className="text-base font-bold tabular-nums" style={{ color: b.color }}>{n}</div>
                <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{b.label}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ModuleBlock({ module, attest, busyId }) {
  return (
    <div className="bg-card border border-border rounded-2xl p-5 mt-4" data-testid={`dias-module-${module.label[0]}`}>
      <h2 className="font-['Outfit'] text-base font-semibold text-foreground mb-3 flex items-center gap-2">
        <Shield className="w-4 h-4" style={{ color: C }} />
        {module.label}
        <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground ml-auto">
          {module.checks.filter(c => c.status === 'pass').length}/{module.checks.length} pass
        </span>
      </h2>

      <ul className="divide-y divide-border">
        {module.checks.map((c, idx) => (
          <CheckRow key={`${c.label}-${idx}`} check={c} attest={attest} busyId={busyId} />
        ))}
      </ul>
    </div>
  );
}

function CheckRow({ check, attest, busyId }) {
  const meta = STATUS_META[check.status] || STATUS_META.manual_only;
  const Icon = meta.icon;
  const ticked = check.status === 'pass';
  const busy = busyId === check.check_id;

  return (
    <li className="py-3 flex items-start gap-3" data-testid={`dias-check-${(check.check_id || check.label).slice(0, 30)}`}>
      {check.manual ? (
        <button onClick={() => attest(check.check_id, !ticked)} disabled={busy}
          data-testid={`dias-attest-${check.check_id}`}
          className="mt-0.5 w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 disabled:opacity-50"
          style={{ borderColor: meta.color, backgroundColor: ticked ? meta.color : 'transparent' }}
          title={ticked ? 'Click to un-attest' : 'Click to attest'}>
          {busy ? <Loader2 className="w-3 h-3 animate-spin text-white" />
            : ticked ? <CheckCircle2 className="w-3.5 h-3.5 text-white" /> : null}
        </button>
      ) : (
        <Icon className="mt-0.5 w-5 h-5 shrink-0" style={{ color: meta.color }} />
      )}

      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <p className="text-sm font-medium text-foreground">{check.label}</p>
          <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded"
            style={{ backgroundColor: `${meta.color}15`, color: meta.color }}>
            {check.manual && !ticked ? 'Awaiting attestation' : meta.label}
          </span>
        </div>
        {check.detail && <p className="text-xs text-muted-foreground mt-0.5">{check.detail}</p>}
        {check.suggestion && check.status !== 'pass' && (
          <p className="text-xs mt-1 inline-flex items-start gap-1" style={{ color: meta.color }}>
            <Sparkles className="w-3 h-3 mt-0.5 shrink-0" /> {check.suggestion}
          </p>
        )}
      </div>
    </li>
  );
}
